import type { GroupedOption } from '@ksyos/design-system';
import { groupBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { IPossibleActivityPerformer } from '@common/interfaces/serviceWorkflow';
import type { IRecipient } from '@common/recipient';
import { getDisplayName } from '@common/utils/getDisplayName';
import { listActivityPerformers } from '@frontend/services/api/activityPerformers';
import { getSelectedLanguage } from '@frontend/translate';
import { useProcessActivityEditContext } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { SelectFormGroup } from '../processActivityFormGroupFields';

type SelectProcessActivityPerformerProps = Readonly<{
    organizationIds?: string[];
    recipientActivityIds: string[];
    serviceId: string;
}>;

export function SelectProcessActivityPerformer(props: SelectProcessActivityPerformerProps) {
    const abortSignal = useAbortSignal();
    const [activityPerformers, setActivityPerformers] = useState<IPossibleActivityPerformer[]>([]);
    const { data, onChange } = useProcessActivityEditContext<{ recipient: IRecipient }>();

    const selectOptions = useMemo(() => {
        const options: GroupedOption[] = [];

        const groupedActivityPerformers = groupBy(activityPerformers, 'organization.name');
        Object.entries(groupedActivityPerformers).forEach((keyValuePair) => {
            const organizationName = keyValuePair[0];
            const activityPerformersGroup = keyValuePair[1];

            // Group header
            options.push({
                isDisabled: true,
                isGroupHeader: true,
                label: organizationName,
                value: organizationName,
            });

            // Group options
            activityPerformersGroup.forEach((activityPerformer) => {
                options.push({
                    isGroupOption: true,
                    label: getDisplayName(activityPerformer.practitioner),
                    value: `${activityPerformer.organization.id}_${activityPerformer.practitioner.id}`,
                });
            });
        });

        return options;
    }, [activityPerformers]);

    useEffect(() => {
        loadActivityPerformers(props.serviceId);
    }, [props.serviceId]);

    return (
        <SelectFormGroup
            fieldKey="recipient.practitionerId"
            isRequired={true}
            label={getSelectedLanguage().common.sendTo}
            onChange={(value) => handleChange(value)}
            options={selectOptions}
            placeholder={`${getSelectedLanguage().common.sendTo}...`}
            value={`${data.recipient.organizationId}_${data.recipient.practitionerId}`}
        />
    );

    function handleChange(value: string | null) {
        let recipient: IRecipient = { organizationId: null, practitionerId: null };
        if (value) {
            const valuesAsArray = value.split('_');
            if (valuesAsArray.length !== 2) {
                throw new Error('Value does not have correct format');
            }
            recipient = { organizationId: valuesAsArray[0], practitionerId: valuesAsArray[1] };
        }
        onChange({ ...data, recipient }, 'recipient.practitionerId');
    }

    async function loadActivityPerformers(serviceId: string) {
        const queryParams = {
            excludePractitioners: false,
            organizationIds: props.organizationIds,
            serviceActivityIds: props.recipientActivityIds,
        };
        const nextActivityPerformers = (await listActivityPerformers(
            serviceId,
            queryParams,
            abortSignal
        )) as unknown as IPossibleActivityPerformer[];
        setActivityPerformers(nextActivityPerformers);
    }
}
